import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorHandler } from '../../helper/error_handler';
import { fetchUserDetailThunk } from '.';
import { fetchUserIntegrationThunk } from './email';
import IntegrationApi from '../../apis/integration';
import { isErrorWithMessage } from '../../helper';
import { resettingIntegrationLoader } from '../slices/integration';
import { RootState } from '../../redux/store';
import { SMTPParams } from '../../params/redux/integration';

export const fetchIntegrationsThunk = createAsyncThunk(
    'integration/fetch',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.fetchIntegrations(state.app.token);
            if (response?.success === 1) {
                return response?.integrations;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const fetchClientIntegrationsThunk = createAsyncThunk(
    'integration/fetch-client',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.fetchClientIntegration(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.integrations;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const fetchCustomerIntegrationsThunk = createAsyncThunk(
    'integration/fetch-customer',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.fetchClientCustomerIntegration(state.app.token, client_id, state.customer.selectedCustomer?.id || 0);
            if (response?.success === 1) {
                return response?.integrations;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const fetchFacebookIntegrationThunks = createAsyncThunk(
    'integration/facebook',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.fetchFacebookIntegration(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.integration;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const removeFacebookIntegrationThunk = createAsyncThunk(
    'integration/remove-facebook',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.removeFacebookIntegration(state.app.token, client_id);
            if (response?.success === 1) {
                await dispatch(fetchFacebookIntegrationThunks(client_id)).unwrap();
                return false;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const removeIntegrationThunk = createAsyncThunk(
    'integration/remove',
    async ({ type, client_id, paramId }: {type: 'calendar' | 'email' | 'meet' | 'twilio' | 'stripe', client_id: number, paramId: string | undefined}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.removeIntegration(state.app.token, type, client_id);
            if (response?.success === 1) {
                if (location.pathname.includes('/client') && state.app.currentUser?.role === 'client') {
                    await dispatch(fetchUserIntegrationThunk(paramId ? parseInt(paramId as string, 10) : 0)).unwrap();
                } else if (location.pathname.includes('/client') && (state.app.currentUser?.role === 'organizationOwner' || state.app.currentUser?.role === 'organizationAdmin' || state.app.currentUser?.role === 'companyManager')) {
                    await dispatch(fetchClientIntegrationsThunk(paramId ? parseInt(paramId as string, 10) : 0)).unwrap();
                } else {
                    await dispatch(fetchIntegrationsThunk()).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const removeStripeIntegrationThunk = createAsyncThunk(
    'integration/remove-stripe',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.removeStripeAccount(state.app.token);
            if (response?.success === 1) {
                await dispatch(fetchIntegrationsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const createTwilioIntegrationThunk = createAsyncThunk(
    'integration/create-twilio',
    async (data: {
        account_sid: string,
        api_sid: string,
        auth_token: string,
        phone_number: string,
        client_id: number,
        paramId: string | undefined
    }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.createTwilioIntegration(state.app.token, data);
            if (response?.success === 1) {
                if (location.pathname.includes('/client') && state.app.currentUser?.role === 'client') {
                    await dispatch(fetchUserIntegrationThunk(data.paramId ? parseInt(data.paramId as string, 10) : 0)).unwrap();
                } else if (location.pathname.includes('/client') && (state.app.currentUser?.role === 'organizationOwner' || state.app.currentUser?.role === 'organizationAdmin' || state.app.currentUser?.role === 'companyManager')) {
                    await dispatch(fetchClientIntegrationsThunk(data.paramId ? parseInt(data.paramId as string, 10) : 0)).unwrap();
                } else {
                    await dispatch(fetchIntegrationsThunk()).unwrap();
                }
                // await dispatch(fetchIntegrationsThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const createSMTPIntegrationThunk = createAsyncThunk(
    'integration/create-smtp',
    async ({ data, paramId }: {data: SMTPParams, paramId: string | undefined}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.connectSMTPIntegration(state.app.token, data);
            if (response?.success === 1) {
                if (location.pathname.includes('/client') && state.app.currentUser?.role === 'client') {
                    await dispatch(fetchUserIntegrationThunk(paramId ? parseInt(paramId as string, 10) : 0)).unwrap();
                } else if (location.pathname.includes('/client') && (state.app.currentUser?.role === 'organizationOwner' || state.app.currentUser?.role === 'organizationAdmin' || state.app.currentUser?.role === 'companyManager')) {
                    await dispatch(fetchClientIntegrationsThunk(paramId ? parseInt(paramId as string, 10) : 0)).unwrap();
                } else {
                    await dispatch(fetchIntegrationsThunk()).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const removeTwilioIntegrationThunk = createAsyncThunk(
    'integration/remove-twilio',
    async ({ client_id, paramId }:{client_id: number, paramId: string | undefined}, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.removeTwilioIntegration(state.app.token, client_id);
            if (response?.success === 1) {
                if (location.pathname.includes('/client') && state.app.currentUser?.role === 'client') {
                    await dispatch(fetchUserIntegrationThunk(paramId ? parseInt(paramId as string, 10) : 0)).unwrap();
                } else if (location.pathname.includes('/client') && (state.app.currentUser?.role === 'organizationOwner' || state.app.currentUser?.role === 'organizationAdmin' || state.app.currentUser?.role === 'companyManager')) {
                    await dispatch(fetchClientIntegrationsThunk(paramId ? parseInt(paramId as string, 10) : 0)).unwrap();
                } else {
                    await dispatch(fetchIntegrationsThunk()).unwrap();
                }
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);

export const createApiKeyThunk = createAsyncThunk(
    'integration/create-api-key',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await IntegrationApi.createApiKey(state.app.token);
            if (response?.success === 1) {
                await dispatch(fetchUserDetailThunk()).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingIntegrationLoader());
        }
    }
);